import { useCallback, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { wishlistSaveEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { BaseBookmarkButtonInner } from 'wishlists/components/Bookmark/Common/BaseBookmarkButton'
import { useBookmarkLabelText } from 'wishlists/components/Bookmark/Buttons/utils'
import { useRemoveWishlistSavedOffer, useWishlistIndex } from '../hooks'
import { buildWishlistFingerprint } from '../fingerprint'
import SaveToWishlistModal from './SaveToWishlistModal'
import type { WishlistBookmarkPayload } from '../types'

interface Props extends Utils.PropsWithDataAttributes {
  item: WishlistBookmarkPayload
  title: string
  imageIds: Array<string>
  subtitle?: string
  imageId?: string
  deeplinkUrl?: string
  locationName?: string
  suggestedWishlistName?: string
  latitude?: number
  longitude?: number
  label?: string
  labelVariant?: 'default' | 'room' | 'search'
  withLabel?: React.ComponentProps<typeof BaseBookmarkButtonInner>['withLabel']
  size?: React.ComponentProps<typeof BaseBookmarkButtonInner>['size']
  iconKind?: React.ComponentProps<typeof BaseBookmarkButtonInner>['iconKind']
  iconVariant?: React.ComponentProps<typeof BaseBookmarkButtonInner>['iconVariant']
  iconColour?: React.ComponentProps<typeof BaseBookmarkButtonInner>['iconColour']
  onShowLogin?: () => void
  onDismissLogin?: () => void
}

function WishlistOfferBookmarkButton({
  item,
  title,
  imageIds,
  subtitle,
  imageId,
  deeplinkUrl,
  locationName,
  suggestedWishlistName,
  latitude,
  longitude,
  label,
  labelVariant = 'default',
  withLabel,
  size,
  iconKind,
  iconVariant,
  iconColour,
  onShowLogin,
  onDismissLogin,
  ...rest
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { data, isFetching } = useWishlistIndex()
  const { mutateAsync: removeWishlistSavedOffer, isLoading: isRemovingSavedOffer } =
    useRemoveWishlistSavedOffer()
  const fingerprint = buildWishlistFingerprint(item)
  const savedState = data?.savedStateIndex[fingerprint]
  const isSaved = Boolean(savedState?.wishlistIds.length)
  const defaultLabelText = useBookmarkLabelText(isSaved, labelVariant)
  const labelText = isSaved ? defaultLabelText : (label ?? defaultLabelText)

  const onOpenModal = useCallback(async() => {
    if (savedState?.savedOfferId) {
      fireInteractionEvent(wishlistSaveEvent('button', 'unsave', item.type))
      try {
        await removeWishlistSavedOffer(savedState.savedOfferId)
        showSnackbar(
          intl.formatMessage({
            id: '5bMyS_FtZP0R1yssB2ld',
            defaultMessage: 'Removed from wishlist',
            description: 'Snackbar shown after removing an offer from all wishlists',
          }),
          'neutral',
        )
      } catch {
        showSnackbar(
          intl.formatMessage({
            id: 'ClTj4g1nLPWel2mm4xhp',
            defaultMessage: 'Could not update this wishlist right now',
            description: 'Error message shown when wishlist membership updates fail',
          }),
          'critical',
        )
      }
      return
    }

    fireInteractionEvent(wishlistSaveEvent('button', 'click', item.type))
    await showModal(
      <SaveToWishlistModal
        item={item}
        title={title}
        imageIds={imageIds}
        subtitle={subtitle}
        imageId={imageId}
        deeplinkUrl={deeplinkUrl}
        locationName={locationName}
        suggestedWishlistName={suggestedWishlistName}
        latitude={latitude}
        longitude={longitude}
        initialSavedState={savedState}
      />,
    )
  }, [
    deeplinkUrl,
    imageId,
    imageIds,
    intl,
    item,
    latitude,
    locationName,
    longitude,
    removeWishlistSavedOffer,
    savedState,
    suggestedWishlistName,
    showModal,
    subtitle,
    title,
  ])

  const onButtonClick = usePendingLoginHandler(
    onOpenModal,
    'wishlistLogin',
    onShowLogin,
    onDismissLogin,
  )

  return (
    <BaseBookmarkButtonInner
      {...rest}
      label={labelText}
      isSaved={isSaved}
      isProcessing={isFetching || isRemovingSavedOffer}
      onClick={onButtonClick}
      withLabel={withLabel}
      size={size}
      iconKind={iconKind}
      iconVariant={iconVariant}
      iconColour={iconColour}
    />
  )
}

export default WishlistOfferBookmarkButton
